// PORTABILIDADE NAS TELAS — rotulos, cor e o que cabe a cada papel.
//
// O rito vive na API (modules/portabilidade) e no dominio
// (`@atlas/domain/portabilidade`). Aqui so' a leitura que as telas fazem dele:
// o mesmo estado se le diferente para quem tem que agir e para quem espera.

import type { PillVariant } from "@atlas/ui/web";
import type { EstadoPortabilidade, Portabilidade } from "@atlas/sdk";

export const ESTADOS_PORTABILIDADE: EstadoPortabilidade[] = [
  "aguardando_proposta",
  "aguardando_servidor",
  "solicitada",
  "aguardando_pagamento",
  "aguardando_liquidacao",
  "concluida",
  "cancelada",
  "expirada",
];

/** Leitura do servidor: o que esta' acontecendo com o contrato dele. */
export const ESTADO_PORTABILIDADE_SERVIDOR: Record<EstadoPortabilidade, string> = {
  aguardando_proposta: "Aguardando a proposta da nova instituição",
  aguardando_servidor: "Confira e aceite a proposta",
  solicitada: "Aguardando a decisão da instituição atual",
  aguardando_pagamento: "Aguardando a nova instituição",
  aguardando_liquidacao: "Aguardando a quitação pela instituição atual",
  concluida: "Concluída",
  cancelada: "Cancelada",
  expirada: "Expirada",
};

/** Leitura de quem opera (banco e averbadora): a etapa do rito. */
export const ESTADO_PORTABILIDADE_OPERADOR: Record<EstadoPortabilidade, string> = {
  aguardando_proposta: "Proposta a apresentar",
  aguardando_servidor: "Aguardando o servidor",
  solicitada: "Aceite e saldo pendentes",
  aguardando_pagamento: "Pagamento a informar",
  aguardando_liquidacao: "Liquidação pendente",
  concluida: "Concluída",
  cancelada: "Cancelada",
  expirada: "Expirada",
};

export function pillDaPortabilidade(estado: EstadoPortabilidade): PillVariant {
  switch (estado) {
    case "aguardando_proposta":
    case "aguardando_servidor":
    case "solicitada":
    case "aguardando_pagamento":
    case "aguardando_liquidacao":
      return "pendente";
    case "concluida":
      return "averbado";
    case "cancelada":
      return "rejeitada";
    default:
      return "expirado";
  }
}

export function portabilidadeEmAberto(p: Pick<Portabilidade, "estado">): boolean {
  return p.estado !== "concluida" && p.estado !== "cancelada" && p.estado !== "expirada";
}

/** O que o BANCO que esta olhando pode fazer agora — sai do papel dele, nao da rota. */
export function acoesDoBancoNaPortabilidade(p: Pick<Portabilidade, "estado" | "papel"> & Partial<Pick<Portabilidade, "condicoesConfirmadasEm" | "valorPago" | "condicoes" | "fluxoLegado" | "pagamentoInformadoEm" | "analiseManual" | "aceiteCredoraEm">>): {
  aceitar: boolean;
  recusar: boolean;
  proposta: boolean;
  pagamento: boolean;
  liquidar: boolean;
  cancelar: boolean;
} {
  return {
    aceitar: p.papel === "credora" && !p.analiseManual && p.estado === "solicitada" && (!!p.condicoesConfirmadasEm || p.fluxoLegado === true),
    recusar: p.papel === "credora" && !p.analiseManual && ["solicitada", "aguardando_pagamento"].includes(p.estado),
    proposta: p.papel === "proponente" && (p.fluxoLegado
      ? p.estado === "aguardando_pagamento" && !p.condicoes
      : portabilidadeEmAberto(p) && !p.condicoesConfirmadasEm && (p.estado !== "aguardando_servidor" || !p.condicoes)),
    pagamento: p.papel === "proponente" && p.estado === "aguardando_pagamento" && (p.fluxoLegado ? !!p.condicoes : !!p.condicoesConfirmadasEm),
    liquidar: p.papel === "credora" && !p.analiseManual && p.estado === "aguardando_liquidacao" && (p.fluxoLegado ? !!p.pagamentoInformadoEm && !!p.condicoes : !!p.condicoesConfirmadasEm && p.valorPago != null),
    // A credora nao cancela: responde ou deixa o prazo vencer.
    cancelar: p.papel === "proponente" && portabilidadeEmAberto(p),
  };
}

export function acoesDoServidorNaPortabilidade(p: Pick<Portabilidade, "estado">): { confirmar: boolean; cancelar: boolean } {
  return {
    confirmar: p.estado === "aguardando_servidor",
    cancelar: portabilidadeEmAberto(p),
  };
}

/** "vence em 3 dias" / "venceu" — o prazo da etapa, para quem precisa agir. */
export function prazoDaEtapa(expiraEm: string | null, agora = Date.now()): string | null {
  if (!expiraEm) return null;
  const ms = new Date(expiraEm).getTime() - agora;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "prazo vencido";
  const horas = Math.floor(ms / 3_600_000);
  if (horas < 24) return `vence em ${Math.max(1, horas)}h`;
  const dias = Math.floor(horas / 24);
  return `vence em ${dias} dia${dias === 1 ? "" : "s"}`;
}

/** "1,85" -> 0.0185. Vazio ou invalido -> null. */
export function percentualParaFracao(v: string): number | null {
  const n = Number(v.trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n / 100 : null;
}
